import React from 'react';

import type { AvailableIconName } from '@/assets/icons';
import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors, withOpacity } from '@/theme/colors';

const strings = STRINGS.guardianProfile.accountSecurities;

type SocialAccountRowProps = {
  icon: AvailableIconName;
  title: string;
  subtitle?: string;
  connected: boolean;
  darkIcon?: boolean;
};

export function SocialAccountRow({
  icon,
  title,
  subtitle,
  connected,
  darkIcon = false,
}: SocialAccountRowProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack direction="row" align="center" style={styles.card}>
      <Stack style={[styles.iconTile, darkIcon ? styles.iconTileDark : null]}>
        <SvgIcon name={icon} size={scale(24)} color={darkIcon ? colors['0'] : undefined} />
      </Stack>
      <Stack flex={1} justify="center">
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
          style={styles.title}
          numberOfLines={1}>
          {title}
        </Text>
        {subtitle && connected ? (
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 11 Regular"
            style={styles.subtitle}
            numberOfLines={1}>
            {subtitle}
          </Text>
        ) : null}
      </Stack>
      <StatusBadge connected={connected} />
    </Stack>
  );
}

function StatusBadge({ connected }: { connected: boolean }) {
  const styles = useStyles();

  return (
    <Stack style={[styles.statusBadge, connected ? styles.statusConnected : styles.statusMuted]}>
      <Text
        variant="Font Guardian App/Subtitle/Subtitle 12 Semibold"
        style={connected ? styles.statusConnectedText : styles.statusMutedText}>
        {connected ? strings.connected : strings.notConnected}
      </Text>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  card: {
    borderRadius: scale(12),
    borderCurve: 'continuous',
    backgroundColor: colors['0'],
    paddingVertical: scale(10),
    paddingRight: scale(16),
    paddingLeft: scale(12),
    gap: scale(10),
    shadowColor: '#222B45',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.04,
    shadowRadius: scale(8),
    elevation: 1,
  },
  iconTile: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(8),
    borderCurve: 'continuous',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: guardianColors['Main/10'],
  },
  iconTileDark: {
    backgroundColor: guardianColors['10'],
  },
  title: {
    color: guardianColors['10'],
  },
  subtitle: {
    color: guardianColors['6'],
  },
  statusBadge: {
    borderRadius: scale(8),
    paddingHorizontal: scale(12),
    paddingVertical: scale(6),
  },
  statusConnected: {
    backgroundColor: withOpacity(guardianColors['13'], 0.26),
  },
  statusMuted: {
    backgroundColor: withOpacity(guardianColors['5'], 0.43),
  },
  statusConnectedText: {
    color: colors['Level 8'],
  },
  statusMutedText: {
    color: guardianColors['6'],
  },
}));
